import { useState } from 'react';
import type { AnnotationAction } from '@/types';
import { cn } from '@/lib/utils';

export interface AnnotationFormProps {
  findingId: string;
  savedAction?: AnnotationAction | null;
  savedComment?: string | null;
  /** Called when the reviewer selects a stance and optionally adds a comment */
  onChange: (findingId: string, action: AnnotationAction, comment: string) => void;
  className?: string;
}

const STANCES: { value: AnnotationAction; label: string; description: string }[] = [
  { value: 'AGREE', label: 'Agree', description: 'This finding is valid' },
  { value: 'DISAGREE', label: 'Disagree', description: 'This finding is incorrect' },
  { value: 'DISCUSS', label: 'Needs Discussion', description: 'Requires further review' },
];

/**
 * Per-finding annotation form: agree / disagree / discuss radio group with
 * optional comment. Saves to local state immediately and propagates to parent
 * for batching with the final review submission.
 *
 * WCAG: Uses fieldset + legend for the radio group, each radio has an explicit label.
 */
export function AnnotationForm({
  findingId,
  savedAction,
  savedComment,
  onChange,
  className,
}: AnnotationFormProps) {
  const [selected, setSelected] = useState<AnnotationAction | null>(savedAction ?? null);
  const [comment, setComment] = useState(savedComment ?? '');

  function handleSelect(action: AnnotationAction) {
    setSelected(action);
    onChange(findingId, action, comment);
  }

  function handleCommentChange(value: string) {
    setComment(value);
    if (selected) {
      onChange(findingId, selected, value);
    }
  }

  return (
    <div className={cn('space-y-3', className)} aria-label={`Annotation for finding ${findingId}`}>
      {/* Radio group — WCAG fieldset/legend */}
      <fieldset>
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Your assessment
        </legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {STANCES.map(({ value, label }) => {
            const isSelected = selected === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                aria-label={label}
                onClick={() => { handleSelect(value); }}
                className={cn(
                  'px-3 py-1.5 rounded-full border text-xs font-medium transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isSelected
                    ? value === 'AGREE'
                      ? 'bg-green-100 border-green-400 text-green-800 dark:bg-green-900/40 dark:border-green-600 dark:text-green-200'
                      : value === 'DISAGREE'
                        ? 'bg-red-100 border-red-400 text-red-800 dark:bg-red-900/40 dark:border-red-600 dark:text-red-200'
                        : 'bg-amber-100 border-amber-400 text-amber-800 dark:bg-amber-900/40 dark:border-amber-600 dark:text-amber-200'
                    : 'bg-background border-border text-muted-foreground hover:border-foreground/40',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Optional comment */}
      {selected && (
        <div>
          <label
            htmlFor={`annotation-comment-${findingId}`}
            className="text-xs font-medium text-muted-foreground block mb-1"
          >
            Comment (optional)
          </label>
          <textarea
            id={`annotation-comment-${findingId}`}
            value={comment}
            onChange={(e) => { handleCommentChange(e.target.value); }}
            placeholder="Add a note about this finding..."
            rows={2}
            className={cn(
              'w-full text-sm rounded-md border border-input bg-background px-3 py-2 resize-none',
              'placeholder:text-muted-foreground focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          />
        </div>
      )}
    </div>
  );
}

/** Small read-only chip showing a previously submitted annotation stance */
export function AnnotationBadge({ action }: { action: AnnotationAction }) {
  const map: Record<AnnotationAction, { label: string; className: string }> = {
    AGREE: { label: 'Agreed', className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700' },
    DISAGREE: { label: 'Disagreed', className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700' },
    DISCUSS: { label: 'Needs Discussion', className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700' },
  };
  const { label, className } = map[action];
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium', className)}>
      {label}
    </span>
  );
}

/** Convenience type for the annotation state map used across the tab */
export type AnnotationMap = Map<string, { action: AnnotationAction; comment: string }>;
